import { crearTabla } from '@plataforma/ui';

import { listarPagosBackoffice, obtenerIngresosResumen } from '../api/backoffice.js';
import { exigirSuperAdmin } from '../guardas.js';

import {
  crearBadge,
  crearPaginacion,
  estadoCarga,
  estadoError,
  ESTADOS_PAGO,
  fecha,
  fechaHora,
  moneda,
  tarjetaMetrica,
} from './backoffice-ui.js';
import { montarShell } from './shell.js';

const RANGOS = {
  hoy: () => {
    const desde = new Date();
    desde.setHours(0, 0, 0, 0);
    return { desde, hasta: new Date() };
  },
  '7d': () => ({ desde: restarDias(7), hasta: new Date() }),
  '30d': () => ({ desde: restarDias(30), hasta: new Date() }),
  mes_actual: () => {
    const ahora = new Date();
    return { desde: new Date(ahora.getFullYear(), ahora.getMonth(), 1), hasta: ahora };
  },
  mes_anterior: () => {
    const ahora = new Date();
    return {
      desde: new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1),
      hasta: new Date(ahora.getFullYear(), ahora.getMonth(), 0, 23, 59, 59, 999),
    };
  },
};

export async function vistaIngresos(contenedorRaiz) {
  if (!exigirSuperAdmin()) return;

  const contenido = montarShell(contenedorRaiz, '/ingresos');
  const estadoVista = { rango: '30d', estadoPago: '', pagina: 1 };

  contenido.innerHTML = `
    <div class="pf-backoffice-header">
      <div>
        <h1>Ingresos</h1>
        <p class="pf-texto-suave">Ingresos reales desde pagos y MRR estimado desde suscripciones.</p>
      </div>
    </div>

    <form class="pf-tarjeta pf-backoffice-toolbar" id="pf-filtros-ingresos">
      <div class="pf-campo">
        <label for="pf-rango-ingresos">Período</label>
        <select id="pf-rango-ingresos">
          <option value="hoy">Hoy</option>
          <option value="7d">7 días</option>
          <option value="30d" selected>30 días</option>
          <option value="mes_actual">Mes actual</option>
          <option value="mes_anterior">Mes anterior</option>
        </select>
      </div>
      <div class="pf-campo">
        <label for="pf-estado-pago">Estado de pago</label>
        <select id="pf-estado-pago">
          <option value="">Todos</option>
          <option value="pendiente">Pendiente</option>
          <option value="pagado">Pagado</option>
          <option value="vencido">Vencido</option>
          <option value="anulado">Anulado</option>
        </select>
      </div>
      <button type="submit" class="pf-boton pf-boton--secundario">Actualizar</button>
    </form>

    <div id="pf-ingresos-resumen">${estadoCarga()}</div>
    <section class="pf-tarjeta pf-backoffice-card" style="margin-top: 16px;">
      <h2>Pagos</h2>
      <div id="pf-pagos-tabla" style="margin-top: 12px;">${estadoCarga()}</div>
    </section>
  `;

  contenido.querySelector('#pf-filtros-ingresos').addEventListener('submit', async (evento) => {
    evento.preventDefault();
    estadoVista.rango = contenido.querySelector('#pf-rango-ingresos').value;
    estadoVista.estadoPago = contenido.querySelector('#pf-estado-pago').value;
    estadoVista.pagina = 1;
    await cargar(contenido, estadoVista);
  });

  await cargar(contenido, estadoVista);
}

async function cargar(contenido, estadoVista) {
  const rango = RANGOS[estadoVista.rango]();
  const [resumen, pagos] = await Promise.all([
    obtenerIngresosResumen({ desde: rango.desde.toISOString(), hasta: rango.hasta.toISOString() }),
    listarPagosBackoffice({ estado: estadoVista.estadoPago, pagina: estadoVista.pagina }),
  ]);

  const resumenEl = contenido.querySelector('#pf-ingresos-resumen');
  if (resumen.error) {
    resumenEl.innerHTML = estadoError('No se pudo cargar el resumen de ingresos.');
  } else {
    const data = resumen.data ?? {};
    resumenEl.innerHTML = `
      <div class="pf-dashboard__tarjetas">
        ${tarjetaMetrica({ titulo: 'Ingresos reales', valor: moneda(data.ingresos_reales), subtitulo: 'Pagos con estado pagado', tono: 'exito' })}
        ${tarjetaMetrica({ titulo: 'MRR estimado', valor: moneda(data.mrr_estimado), subtitulo: 'Suscripciones activas x plan', tono: 'alerta' })}
        ${tarjetaMetrica({ titulo: 'Recurrente estimado', valor: moneda(data.ingresos_recurrentes_estimados), subtitulo: 'No es ingreso cobrado', tono: 'info' })}
        ${tarjetaMetrica({ titulo: 'Contrataciones', valor: data.nuevas_contrataciones ?? 0, subtitulo: `${data.aprobaciones ?? 0} aprobadas`, tono: 'info' })}
      </div>
      <p class="pf-backoffice-note" style="margin-top: 12px;">
        No hay gateway financiero integrado. Las métricas marcadas como estimadas se calculan desde suscripciones y planes, no desde cobros reales.
      </p>
    `;
  }

  const destino = contenido.querySelector('#pf-pagos-tabla');
  if (pagos.error) {
    destino.innerHTML = estadoError('No se pudieron cargar los pagos.');
    return;
  }

  destino.innerHTML = '';
  destino.appendChild(
    crearTabla({
      filaVacia: 'No hay pagos registrados.',
      columnas: [
        { titulo: 'Empresa', render: (fila) => fila.empresa?.nombre ?? '—' },
        { titulo: 'Periodo', render: (fila) => fecha(fila.periodo) },
        { titulo: 'Monto', render: (fila) => moneda(fila.monto) },
        { titulo: 'Estado', render: (fila) => crearBadge(ESTADOS_PAGO, fila.estado) },
        { titulo: 'Método', render: (fila) => fila.metodo ?? '—' },
        { titulo: 'Fecha pago', render: (fila) => fechaHora(fila.fecha_pago) },
      ],
      filas: pagos.data ?? [],
    }),
  );
  destino.appendChild(
    crearPaginacion({
      total: pagos.count ?? 0,
      pagina: estadoVista.pagina,
      alCambiar: async (pagina) => {
        estadoVista.pagina = pagina;
        await cargar(contenido, estadoVista);
      },
    }),
  );
}

function restarDias(dias) {
  return new Date(Date.now() - dias * 86_400_000);
}
